import { CommonModule } from "@angular/common";
import { Component, DestroyRef, inject, NgZone } from "@angular/core";
import { FormBuilder, ReactiveFormsModule } from "@angular/forms";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";

import { JslibModule } from "@bitwarden/angular/jslib.module";
import { LoginComponent, LoginComponentService } from "@bitwarden/auth/angular";
import {
  LoginEmailServiceAbstraction,
  LoginStrategyServiceAbstraction,
  LoginSuccessHandlerService,
} from "@bitwarden/auth/common";
import { InternalPolicyService } from "@bitwarden/common/admin-console/abstractions/policy/policy.service.abstraction";
import { DevicesApiServiceAbstraction } from "@bitwarden/common/auth/abstractions/devices-api.service.abstraction";
import { SsoLoginServiceAbstraction } from "@bitwarden/common/auth/abstractions/sso-login.service.abstraction";
import { PasswordPreloginService } from "@bitwarden/common/auth/password-prelogin";
import { AppIdService } from "@bitwarden/common/platform/abstractions/app-id.service";
import { BroadcasterService } from "@bitwarden/common/platform/abstractions/broadcaster.service";
import { ConfigService } from "@bitwarden/common/platform/abstractions/config/config.service";
import { EnvironmentService } from "@bitwarden/common/platform/abstractions/environment.service";
import { I18nService } from "@bitwarden/common/platform/abstractions/i18n.service";
import { LogService } from "@bitwarden/common/platform/abstractions/log.service";
import { MessagingService } from "@bitwarden/common/platform/abstractions/messaging.service";
import { PlatformUtilsService } from "@bitwarden/common/platform/abstractions/platform-utils.service";
import { ValidationService } from "@bitwarden/common/platform/abstractions/validation.service";
import { DefaultServerSettingsService } from "@bitwarden/common/platform/services/default-server-settings.service";
import { PasswordStrengthServiceAbstraction } from "@bitwarden/common/tools/password-strength";
import {
  AnonLayoutWrapperDataService,
  AsyncActionsModule,
  ButtonModule,
  CheckboxModule,
  FormFieldModule,
  IconButtonModule,
  LinkModule,
  ToastService,
} from "@bitwarden/components";
// FIXME(https://bitwarden.atlassian.net/browse/CL-764): Migrate to OnPush
// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
  templateUrl: "./web-login.component.html",
  styles: [
    `
      button.brackets-auth-primary-button:not(:disabled) {
        background-color: rgb(96, 57, 187) !important;
        border-color: rgb(96, 57, 187) !important;
      }

      button.brackets-auth-primary-button:not(:disabled):hover,
      button.brackets-auth-primary-button:not(:disabled):focus-visible {
        background-color: rgb(79, 47, 156) !important;
        border-color: rgb(79, 47, 156) !important;
      }
    `,
  ],
  imports: [
    AsyncActionsModule,
    ButtonModule,
    CheckboxModule,
    CommonModule,
    FormFieldModule,
    IconButtonModule,
    LinkModule,
    JslibModule,
    ReactiveFormsModule,
    RouterModule,
  ],
})
export class WebLoginComponent extends LoginComponent {
  private webLoginEmailService = inject(LoginEmailServiceAbstraction);
  private layoutDataService = inject(AnonLayoutWrapperDataService);
  private serverSettingsService = inject(DefaultServerSettingsService);

  protected isUserRegistrationDisabled$ = this.serverSettingsService.isUserRegistrationDisabled$;

  constructor(
    activatedRoute: ActivatedRoute,
    anonLayoutWrapperDataService: AnonLayoutWrapperDataService,
    appIdService: AppIdService,
    broadcasterService: BroadcasterService,
    destroyRef: DestroyRef,
    devicesApiService: DevicesApiServiceAbstraction,
    formBuilder: FormBuilder,
    i18nService: I18nService,
    loginEmailService: LoginEmailServiceAbstraction,
    loginComponentService: LoginComponentService,
    loginStrategyService: LoginStrategyServiceAbstraction,
    messagingService: MessagingService,
    ngZone: NgZone,
    passwordStrengthService: PasswordStrengthServiceAbstraction,
    platformUtilsService: PlatformUtilsService,
    policyService: InternalPolicyService,
    router: Router,
    toastService: ToastService,
    logService: LogService,
    validationService: ValidationService,
    loginSuccessHandlerService: LoginSuccessHandlerService,
    configService: ConfigService,
    ssoLoginService: SsoLoginServiceAbstraction,
    environmentService: EnvironmentService,
    passwordPreloginService: PasswordPreloginService,
  ) {
    super(
      activatedRoute,
      anonLayoutWrapperDataService,
      appIdService,
      broadcasterService,
      destroyRef,
      devicesApiService,
      formBuilder,
      i18nService,
      loginEmailService,
      loginComponentService,
      loginStrategyService,
      messagingService,
      ngZone,
      passwordStrengthService,
      platformUtilsService,
      policyService,
      router,
      toastService,
      logService,
      validationService,
      loginSuccessHandlerService,
      configService,
      ssoLoginService,
      environmentService,
      passwordPreloginService,
    );
  }

  override async ngOnInit(): Promise<void> {
    await super.ngOnInit();

    this.layoutDataService.setAnonLayoutWrapperData({
      pageTitle: { key: "loginPageEmailEntryScreenTitle" },
      pageSubtitle: null,
    });
  }

  singleStepSubmit = async (): Promise<void> => {
    if (!this.validateEmail()) {
      return;
    }

    this.formGroup.markAllAsTouched();
    if (this.formGroup.invalid) {
      return;
    }

    const email = this.formGroup.value.email;
    if (email) {
      await this.webLoginEmailService.setLoginEmail(email);
      await this.webLoginEmailService.setRememberedEmailChoice(
        email,
        this.formGroup.value.rememberEmail ?? false,
      );
    }

    await this.submit();
  };
}
